import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import useAuth from '../../context/auth.context.jsx';
import useGroup from '../../context/group.context.jsx';
import groupServices from '../services/groupServices';
import userService from '../services/userService';
import Sheet from './common/sheet.jsx';
import Button from './common/button.jsx';
import { canShowPricing } from '../utils/pricing.js';
import { noticeDays as OVER_LIMIT_NOTICE_DAYS, graceDaysAfterT0 as OVER_LIMIT_GRACE_DAYS_AFTER_T0 } from '@shared/overLimit.json';

/**
 * Mounted once in App.jsx's ProtectedShell, right after
 * PendingInviteConsumer — so it covers every group-scoped page, not just
 * one. Reads userController.getMe's `entitlements` block.
 *
 * When OVER_LIMIT_READ_ONLY is on:
 * - BANNER (grace): settle-up copy + picker for which N groups stay writable.
 * - MODAL (T0+graceDaysAfterT0 / A+60 inclusive): view-only explanation. Dismissible so settle-up and
 *   export stay reachable. Does not push archive/leave.
 *
 * When the write-gate is off, the paywall-era archive/leave modal is unchanged.
 */
function GroupPicker({ entitlements, groups, t, onSaved }) {
    const limit = entitlements.writableGroupLimit;
    const [selected, setSelected] = useState(() => new Set(entitlements.writableGroupIds || []));
    const [busy, setBusy] = useState(false);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    const save = async () => {
        setBusy(true);
        try {
            await userService.setWritableGroups([...selected]);
            toast.success(t('planLimitPickSaved'));
            await onSaved();
        } catch (error) {
            toast.error(error.response?.data?.error || t('planLimitActionFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!limit || !groups.length) return null;
    const ready = selected.size === limit;
    const cooldown = entitlements.nextWritablePickAt;

    return (
        <div className="plan-limit-picker">
            <p className="fw-semibold">{t('planLimitPickTitle', { limit })}</p>
            <p className="text-muted-ms">{t('planLimitPickHelp')}</p>
            <ul className="list-unstyled mb-2">
                {groups.map((g) => {
                    const id = String(g._id);
                    const on = selected.has(id);
                    return (
                        <li key={id}>
                            <label className="d-flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={on}
                                    disabled={!entitlements.canPickWritableGroups && !on}
                                    onChange={() => toggle(id)}
                                />
                                <span>{g.name}</span>
                                <span className="text-muted-ms">{on ? t('planLimitWritable') : t('planLimitLocked')}</span>
                            </label>
                        </li>
                    );
                })}
            </ul>
            {cooldown && (
                <p className="text-muted-ms">{t('planLimitPickCooldown', { date: new Date(cooldown).toLocaleDateString() })}</p>
            )}
            {entitlements.canPickWritableGroups && (
                <Button text={t('planLimitPickSave')} onClick={save} disabled={busy || !ready} />
            )}
        </div>
    );
}

export default function PlanLimitBanner() {
    const { t } = useTranslation(['common', 'groups']);
    const navigate = useNavigate();
    const { userData, loadUserData } = useAuth();
    const { refreshGroups, myGroups } = useGroup();
    const [busyId, setBusyId] = useState(null);
    const [resolvedIds, setResolvedIds] = useState(() => new Set());
    const [locallyResolved, setLocallyResolved] = useState(false);
    const [dismissedReadOnly, setDismissedReadOnly] = useState(false);

    const entitlements = userData?.entitlements;
    if (!entitlements || entitlements.graceState === 'NONE' || locallyResolved) return null;

    const { graceState, limits, usage, actionableGroups = [] } = entitlements;
    const writeGateOn = !!entitlements.overLimitReadOnlyEnabled;
    const remaining = actionableGroups.filter((g) => !resolvedIds.has(g.groupId));
    const requiredToResolve = Math.max(0, (usage?.activeGroups || 0) - (limits?.activeGroups || 0));

    const handleAction = async (group) => {
        setBusyId(group.groupId);
        try {
            if (group.action === 'archive') await groupServices.archiveGroup(group.groupId);
            else await groupServices.leaveGroup(group.groupId);

            const next = new Set(resolvedIds);
            next.add(group.groupId);
            setResolvedIds(next);
            if (next.size >= requiredToResolve) setLocallyResolved(true);

            await Promise.all([refreshGroups(), loadUserData()]);
        } catch (error) {
            toast.error(error.response?.data?.error || t('planLimitActionFailed'));
        } finally {
            setBusyId(null);
        }
    };

    const banner = (
        <div className="plan-limit-banner" role="status">
            <i className="bi bi-exclamation-circle" />
            <span>{t('planLimitBannerText', { count: usage.activeGroups, limit: limits.activeGroups, noticeDays: OVER_LIMIT_NOTICE_DAYS, graceDays: OVER_LIMIT_GRACE_DAYS_AFTER_T0 })}</span>
            {canShowPricing && (
                <button type="button" className="btn-ms btn-ms-premium btn-ms-sm" onClick={() => navigate('/plans')}>
                    {t('planLimitUpgradeCta')}
                </button>
            )}
        </div>
    );

    if (writeGateOn) {
        const picker = (
            <GroupPicker
                entitlements={entitlements}
                groups={myGroups || []}
                t={t}
                onSaved={async () => { await loadUserData(); }}
            />
        );
        if (graceState === 'BANNER') {
            return (
                <div>
                    {banner}
                    {picker}
                </div>
            );
        }
        if (dismissedReadOnly) return banner;
        return (
            <>
                {banner}
                <Sheet open title={t('planLimitReadOnlyTitle')} dismissible onClose={() => setDismissedReadOnly(true)}>
                    <p className="text-muted-ms">{t('planLimitReadOnlyBody')}</p>
                    {picker}
                </Sheet>
            </>
        );
    }

    if (graceState === 'BANNER') return banner;

    return (
        <Sheet open title={t('planLimitModalTitle')} dismissible={false}>
            <p className="text-muted-ms">{t('planLimitModalBody', { limit: limits.activeGroups, noticeDays: OVER_LIMIT_NOTICE_DAYS, graceDays: OVER_LIMIT_GRACE_DAYS_AFTER_T0 })}</p>
            {remaining.map((group) => (
                <div key={group.groupId} className="plan-limit-modal-row">
                    <span>{group.name}</span>
                    <Button
                        text={t(group.action === 'archive' ? 'groups:archive' : 'groups:leaveGroup')}
                        onClick={() => handleAction(group)}
                        disabled={busyId === group.groupId}
                    />
                </div>
            ))}
        </Sheet>
    );
}
